// Card views for the WS-3.A core (foundation + data) kinds emitted by
// clinisharp-agents tools. These are the generic rendering primitives —
// most copilot/cowork tools route through one of these instead of having
// a tool-specific renderer.
//
// Pydantic source of truth lives at:
//   clinisharp-agents/src/clinisharp_agents/contracts/cards/data.py
//   clinisharp-agents/src/clinisharp_agents/contracts/cards/foundation.py
//
// Wire registration in core/index.ts so the side-effect import in App.tsx
// populates the CardRegistry on app boot.

import { useMemo } from "react";
import { chartColors, chartSeries } from "@/lib/chart-colors";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Calendar,
  CheckCircle,
  Lightbulb,
  ListChecks,
  TrendingDown,
  TrendingUp,
  Minus,
  AlertCircle,
} from "lucide-react";
import type {
  AgendaTimelineCardEnvelope,
  ChartCardEnvelope,
  ConfirmationCardEnvelope,
  DataPoint,
  InsightCardEnvelope,
  TableCardEnvelope,
  ToolCompletedCardEnvelope,
} from "@/components/generative-ui/envelope/types";

const CHART_PALETTE = [...chartSeries, chartColors.danger];

function CardShell({
  icon: Icon,
  iconBg,
  title,
  subtitle,
  children,
}: {
  icon: React.ElementType;
  iconBg: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  // A resting card is a hairline on the surface: no glass, no glow (design
  // system section 4). Elevation is reserved for what floats.
  return (
    <div className="space-y-3 rounded-xl border border-app-border bg-app-surface p-4 shadow-premium-xs">
      <div className="flex items-center gap-2">
        <div className={`flex size-7 items-center justify-center rounded-lg ${iconBg}`}>
          <Icon className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-app-fg-strong">{title}</p>
          {subtitle && <p className="truncate text-[13px] text-app-fg-muted">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ── ChartCard (kind=chart) ── */
export function ChartCardView({ card }: { card: ChartCardEnvelope }) {
  // Recharts wants an array of objects keyed by series-label per x-bucket.
  // Pivot the [{label, points: [{x, y}]}] shape into [{x, A: yA, B: yB, ...}].
  const chartData = useMemo(() => {
    const xs = new Set<string | number>();
    card.series.forEach((s) => s.points.forEach((p) => xs.add(p.x)));
    return Array.from(xs).map((x) => {
      const row: Record<string, string | number> = { x: typeof x === "string" ? x : String(x) };
      card.series.forEach((s) => {
        const p = s.points.find((pp) => pp.x === x);
        if (p) row[s.label] = p.y;
      });
      return row;
    });
  }, [card.series]);

  // An empty chart is a state, not a blank frame: the clinic reads why there
  // is nothing to draw, and the card keeps its own description.
  const hasPoints = card.series.some((serie) => serie.points.length > 0);
  if (!hasPoints) {
    return (
      <CardShell icon={ListChecks} iconBg="bg-brand-soft text-brand" title={card.title} subtitle={card.description ?? undefined}>
        <div className="rounded-lg bg-accent-warm-soft px-4 py-8 text-center" data-testid="chart-empty-state">
          <p className="font-display text-[15px] text-app-fg-strong">Sem dados no período</p>
        </div>
      </CardShell>
    );
  }

  const ChartImpl =
    card.chart_type === "line" ? LineChart
      : card.chart_type === "area" ? AreaChart
      : card.chart_type === "pie" ? PieChart
      : BarChart;

  if (card.chart_type === "pie") {
    const flat = card.series[0]?.points.map((p, i) => ({
      name: typeof p.x === "string" ? p.x : String(p.x),
      value: p.y,
      fill: CHART_PALETTE[i % CHART_PALETTE.length],
    })) ?? [];
    return (
      <CardShell icon={ListChecks} iconBg="bg-brand-soft text-brand" title={card.title} subtitle={card.description ?? undefined}>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={flat} dataKey="value" nameKey="name" outerRadius={70} label />
            {flat.map((d, i) => <Cell key={i} fill={d.fill} />)}
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </CardShell>
    );
  }

  return (
    <CardShell icon={ListChecks} iconBg="bg-brand-soft text-brand" title={card.title} subtitle={card.description ?? undefined}>
      <ResponsiveContainer width="100%" height={180}>
        <ChartImpl data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="x" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip />
          {card.series.map((s, i) => {
            const color = CHART_PALETTE[i % CHART_PALETTE.length];
            if (card.chart_type === "line") {
              return <Line key={s.label} dataKey={s.label} stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} />;
            }
            if (card.chart_type === "area") {
              return <Area key={s.label} dataKey={s.label} stroke={color} fill={color} fillOpacity={0.2} />;
            }
            return <Bar key={s.label} dataKey={s.label} fill={color} radius={[4, 4, 0, 0]} />;
          })}
        </ChartImpl>
      </ResponsiveContainer>
    </CardShell>
  );
}

/* ── TableCard (kind=table) ── */
export function TableCardView({ card }: { card: TableCardEnvelope }) {
  const subtitle = card.total_count > card.rows.length
    ? `${card.rows.length} de ${card.total_count}`
    : `${card.total_count} resultado${card.total_count === 1 ? "" : "s"}`;

  if (!card.rows || card.rows.length === 0) {
    return (
      <CardShell icon={ListChecks} iconBg="bg-app-surface-muted text-app-fg-muted" title="Resultados" subtitle="0 registros">
        <p className="py-6 text-center text-[13px] text-app-fg-muted">
          {card.empty_state_message ?? "Nenhum resultado encontrado."}
        </p>
      </CardShell>
    );
  }

  return (
    <CardShell icon={ListChecks} iconBg="bg-app-surface-muted text-app-fg-muted" title="Resultados" subtitle={subtitle}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-app-border">
              {card.columns.map((c) => (
                <th key={c} className="px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-app-fg-muted">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {card.rows.map((row, i) => (
              <tr key={i} className="border-b border-app-border-subtle last:border-0">
                {row.map((cell, j) => (
                  <td key={j} className="px-2 py-1.5 tabular-nums text-app-fg">{String(cell ?? "—")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

/* ── AgendaTimelineCard (kind=agenda.timeline) ── */
const AGENDA_STATUS_COLORS: Record<string, string> = {
  confirmed: "bg-app-success",
  confirmado: "bg-app-success",
  pending: "bg-app-warning",
  pendente: "bg-app-warning",
  cancelled: "bg-app-danger",
  cancelado: "bg-app-danger",
};

export function AgendaTimelineCardView({ card }: { card: AgendaTimelineCardEnvelope }) {
  if (!card.slots || card.slots.length === 0) {
    return (
      <CardShell icon={Calendar} iconBg="bg-brand-soft text-brand" title={`Agenda — ${card.date}`} subtitle="Sem horários">
        <p className="text-[13px] text-app-fg-muted text-center py-6">Nenhum agendamento neste dia</p>
      </CardShell>
    );
  }
  return (
    <CardShell icon={Calendar} iconBg="bg-brand-soft text-brand" title={`Agenda — ${card.date}`} subtitle={card.description ?? `${card.slots.length} horário(s)`}>
      <ul className="space-y-2">
        {card.slots.map((s, i) => (
          <li key={`${s.time}-${i}`} className="flex items-start gap-2 text-xs">
            <span className="font-mono text-app-fg shrink-0 w-12">{s.time}</span>
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${AGENDA_STATUS_COLORS[s.status?.toLowerCase()] ?? "bg-app-fg-subtle"}`} />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-app-fg-strong truncate">{s.patient_name}</p>
              <p className="text-app-fg-muted truncate">{s.procedure}</p>
            </div>
            {s.no_show_risk && (
              <span className="text-[10px] uppercase font-semibold text-app-warning shrink-0">No-show?</span>
            )}
          </li>
        ))}
      </ul>
    </CardShell>
  );
}

/* ── ToolCompletedCard (kind=tool.completed) ── */
const UNDO_HINT_PLACEHOLDER = /\{[^{}]+\}/;
const UNDO_HINT_PLACEHOLDERS = /\{[^{}]+\}/g;

/**
 * The runtime contract forbids concrete ids in `undo_hint`, so it arrives as a path
 * template ("POST /patients/{patient_id}/restore"). Show the clinic the real id when
 * `reference_id` carries it, and say nothing rather than show a raw template.
 */
function resolveUndoHint(card: ToolCompletedCardEnvelope): string | null {
  const hint = card.undo_hint?.trim();
  if (!hint) return null;
  if (!UNDO_HINT_PLACEHOLDER.test(hint)) return hint;
  if (!card.reference_id) return null;
  return hint.replace(UNDO_HINT_PLACEHOLDERS, card.reference_id);
}

export function ToolCompletedCardView({ card }: { card: ToolCompletedCardEnvelope }) {
  const undoHint = resolveUndoHint(card);
  return (
    <CardShell icon={CheckCircle} iconBg="bg-app-success/10 text-app-success" title="Concluído" subtitle={card.action_name}>
      <p className="text-[15px] leading-relaxed text-app-fg">{card.result_summary}</p>
      <div className="flex items-center justify-between text-[13px] text-app-fg-muted">
        <span>{card.affected_count} afetado{card.affected_count === 1 ? "" : "s"}</span>
        {card.reference_id && <span className="font-mono truncate ml-2">{card.reference_id}</span>}
      </div>
      {undoHint && (
        <p
          data-testid="tool-completed-undo-hint"
          className="text-[13px] text-app-fg-muted border-t border-app-border-subtle pt-2"
        >
          <span className="font-medium">Desfazer:</span> {undoHint}
        </p>
      )}
    </CardShell>
  );
}

/* ── ConfirmationCard (kind=confirmation) ── */
export function ConfirmationCardView({ card }: { card: ConfirmationCardEnvelope }) {
  return (
    <CardShell icon={AlertCircle} iconBg="bg-app-warning/10 text-app-warning" title="Aguardando confirmação" subtitle={card.details?.action}>
      <p className="text-[15px] leading-relaxed text-app-fg-strong">{card.summary}</p>
      <p className="text-[13px] text-app-fg-muted">Nada foi salvo ainda. Revise e aprove.</p>
      {card.details && (card.details.count != null || card.details.entity_label) && (
        <p className="text-[13px] text-app-fg-muted">
          {card.details.entity_label}{card.details.entity_label && card.details.count != null ? " · " : ""}
          {card.details.count != null ? `${card.details.count} item(ns)` : ""}
        </p>
      )}
    </CardShell>
  );
}

/* ── InsightCard (kind=insight) ── */
function trendIcon(trend?: DataPoint["trend"]) {
  if (trend === "up") return <TrendingUp className="w-3 h-3 text-app-success" />;
  if (trend === "down") return <TrendingDown className="w-3 h-3 text-app-danger" />;
  if (trend === "neutral") return <Minus className="w-3 h-3 text-app-fg-muted" />;
  return null;
}

export function InsightCardView({ card }: { card: InsightCardEnvelope }) {
  return (
    <CardShell icon={Lightbulb} iconBg="bg-app-warning/10 text-app-warning" title={card.title}>
      <p className="text-[15px] leading-relaxed text-app-fg">{card.body}</p>
      {card.data_points && card.data_points.length > 0 && (
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-app-border-subtle">
          {card.data_points.map((dp, i) => (
            <div key={i} className="text-xs">
              <p className="text-app-fg-muted">{dp.label}</p>
              <p className="font-semibold text-app-fg-strong flex items-center gap-1">
                {dp.value} {trendIcon(dp.trend)}
              </p>
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
}

